import {
  getAlertBanner,
  GET_ALERT_BANNER_SUCCESS,
  updateAlertBanner,
  UPDATE_ALERT_BANNER_FAIL,
} from 'actions/banners'
import { alert } from 'actions/flash'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import { AlertBanner } from 'models/Banner'
import { useCallback, useEffect, useRef, useState } from 'react'

const t = buildTranslate('cms.content.alert_banner')

type UseAlertBannerFormOptions<T> = {
  mapFromBanner: (banner: AlertBanner) => T
  mapToPayload: (formModel: T, banner: AlertBanner) => Partial<AlertBanner>
  // When false, skip the initial fetch. Used by scenes that may mount
  // briefly during a redirect (flag-off case) — we don't want to fire
  // the GET only to unmount before using the response.
  enabled?: boolean
}

export function useAlertBannerForm<T>({
  mapFromBanner,
  mapToPayload,
  enabled = true,
}: UseAlertBannerFormOptions<T>) {
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const bannerRef = useRef<AlertBanner | null>(null)
  const [formModel, setFormModel] = useState<T | null>(null)
  const dispatch = useDispatch()

  // Hold mappers in refs so consumers can define them inline without
  // re-triggering the fetch effect (which would clobber in-progress edits
  // and risk an infinite loop) or destabilising the publishChanges
  // callback.
  const mapFromBannerRef = useRef(mapFromBanner)
  const mapToPayloadRef = useRef(mapToPayload)
  useEffect(() => {
    mapFromBannerRef.current = mapFromBanner
    mapToPayloadRef.current = mapToPayload
  })

  const publishChanges = useCallback(
    async (currentFormModel: T) => {
      if (!bannerRef.current) return
      setIsSubmitting(true)
      const response = await dispatch(
        updateAlertBanner(
          mapToPayloadRef.current(currentFormModel, bannerRef.current)
        )
      )

      setIsSubmitting(false)
      setIsModalOpen(false)

      if (response.type === UPDATE_ALERT_BANNER_FAIL) {
        dispatch(
          alert({
            key: 'danger',
            message: t('submit_error'),
            timeout: 5,
          })
        )
        return
      }

      dispatch(
        alert({
          key: 'success',
          message: t('submit_success'),
          timeout: 5,
        })
      )
    },
    [dispatch]
  )

  useEffect(() => {
    if (!enabled) return
    async function fetchData() {
      setIsLoading(true)
      setLoadError(false)
      const response = await dispatch(getAlertBanner())

      if (response.type === GET_ALERT_BANNER_SUCCESS) {
        const banner = response.payload.data.alert_banner
        bannerRef.current = banner
        setFormModel(mapFromBannerRef.current(banner))
      } else {
        // Any non-success (network error, 4xx/5xx, etc). Surface it so the
        // scene can render an error state instead of an infinite spinner.
        setLoadError(true)
      }

      setIsLoading(false)
    }

    fetchData()
  }, [dispatch, enabled])

  return {
    formModel,
    setFormModel,
    isLoading,
    loadError,
    isSubmitting,
    isModalOpen,
    setIsModalOpen,
    publishChanges,
  }
}
